import type { AuthProvider } from '@refinedev/core';
import simpleRest, { axiosInstance } from '@refinedev/simple-rest';

/** Same origin: the Vite proxy in development, the TLS proxy in production. */
export const API_URL = '/admin/v1';

/**
 * The read-only admin signs in with an API client token that has the
 * `status` scope (`cli api-client add <uid> <name> read,status`). It lives in
 * this tab's session storage only. OIDC replaces it once the identity
 * provider is decided (docs/sports-data-service.md §7.3).
 */
const KEY = 'c1-sports-admin-token';

function readToken(): string | null {
	try {
		return sessionStorage.getItem( KEY );
	} catch {
		return null;
	}
}

function writeToken( token: string | null ): void {
	try {
		if ( token ) {
			sessionStorage.setItem( KEY, token );
		} else {
			sessionStorage.removeItem( KEY );
		}
	} catch {
		// Storage blocked: the session ends with the page.
	}
}

let memoryToken: string | null = readToken();
const token = () => memoryToken ?? readToken();

axiosInstance.interceptors.request.use( ( config ) => {
	const current = token();
	if ( current ) {
		config.headers.set( 'Authorization', `Bearer ${ current }` );
	}
	return config;
} );

export const dataProvider = simpleRest( API_URL, axiosInstance );

export interface Me {
	id: string | null;
	name: string | null;
	scopes: string[];
}

async function me( candidate: string ): Promise<Me | { error: string }> {
	const response = await fetch( `${ API_URL }/me`, { headers: { authorization: `Bearer ${ candidate }` } } );
	const body = await response.json().catch( () => ( {} ) );
	return response.ok ? ( body as Me ) : { error: String( body.message ?? `HTTP ${ response.status }` ) };
}

export const authProvider: AuthProvider = {
	login: async ( { token: candidate }: { token?: string } ) => {
		const value = candidate?.trim() ?? '';
		const result = value ? await me( value ) : { error: 'Enter a token.' };
		if ( 'error' in result ) {
			return { success: false, error: { name: 'Sign-in failed', message: result.error } };
		}
		memoryToken = value;
		writeToken( value );
		return { success: true, redirectTo: '/' };
	},
	logout: async () => {
		memoryToken = null;
		writeToken( null );
		return { success: true, redirectTo: '/login' };
	},
	check: async () => ( token() ? { authenticated: true } : { authenticated: false, redirectTo: '/login', logout: true } ),
	onError: async ( error ) => {
		const status = error?.statusCode ?? error?.response?.status;
		return status === 401 || status === 403 ? { logout: true, redirectTo: '/login', error } : {};
	},
	getIdentity: async () => {
		const current = token();
		if ( ! current ) {
			return null;
		}
		const result = await me( current );
		return 'error' in result ? null : { id: result.id, name: result.name ?? result.id };
	},
};
